import LoadingScreen from '@/components/common/LoadingScreen'
import AuthProvider from '@/features/auth/provider'
import { useTranslation } from '@/hooks/useTranslation'
import { SignalRProvider } from '@/signalr/context'
import store from '@/store/stores'
import { ThemeProvider } from '@/theme'
import { CssBaseline, StyledEngineProvider } from '@mui/material'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { Suspense, useEffect, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Provider } from 'react-redux'
import AppRoute from './AppRoute'
import { ErrorBoundary } from './ErrorBoundary'
import { GlobalHoverSidebar } from '@/components/layout/GlobalHoverSidebar'
import { MotionConfig } from 'framer-motion'

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      // 🎉 only show error toasts if we already have data in the cache
      // which indicates a failed background update
      // if (error?.response?.status === 401) {
      //   localStorage.clear()
      //   window.location.href = '/'
      // }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
})

function LanguageAttributeManager() {
  const { currentLanguage } = useTranslation()

  useEffect(() => {
    document.documentElement.setAttribute('data-lang', currentLanguage)
  }, [currentLanguage])

  return null
}

function App() {
  const [isFirstApp, setIsFirstApp] = useState(false)

  useEffect(() => {
    createKeyMap()
  }, [])

  const createKeyMap = () => {
    setTimeout(() => {
      setIsFirstApp(true)
    }, 2000)
  }
  if (!isFirstApp) return <LoadingScreen />

  return (
    <Provider store={store}>
      {/* reducedMotion="user" makes framer honour the OS "reduce motion"
          setting everywhere at once: transform and layout animations are
          skipped, opacity ones still play, so content fades in without
          travelling. Setting it here rather than per component keeps the ~135
          motion call sites from each needing their own guard. */}
      <MotionConfig reducedMotion="user">
        <ThemeProvider>
          <ErrorBoundary>
            <SignalRProvider>
              <StyledEngineProvider injectFirst>
                <Suspense fallback={<LoadingScreen />}>
                  <CssBaseline />
                  <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                      <LanguageAttributeManager />
                      <GlobalHoverSidebar>
                        <AppRoute />
                      </GlobalHoverSidebar>
                    </AuthProvider>
                    {/* <ReactQueryDevtools initialIsOpen={false} /> */}
                  </QueryClientProvider>
                </Suspense>
              </StyledEngineProvider>
            </SignalRProvider>
            {/* react-hot-toast paints white by default, which lands as a
                bright block over the dark theme. CSS variables (set by
                injectThemeCSSVariables) rather than the MUI theme: the Toaster
                sits outside the ThemeProvider's consumer tree here. */}
            <Toaster
              position="top-right"
              reverseOrder={false}
              toastOptions={{
                style: {
                  background: 'var(--background-overrideDefault, #fff)',
                  color: 'var(--text-main, #1f2937)',
                  border: '1px solid var(--border-main, transparent)',
                },
                // Server refusals are whole sentences ("This Plant cannot be
                // deleted because it is being used for a Station"); the 4s
                // default is not long enough to read one and act on it.
                error: { duration: 8000 },
              }}
            />
          </ErrorBoundary>
        </ThemeProvider>
      </MotionConfig>
    </Provider>
  )
}

export default App
